import { useState, useEffect } from 'react';
import { 
  signInWithPopup, 
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
  applyActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [showSignUp, setShowSignUp] = useState(false);
  const [userType, setUserType] = useState<'dealer' | 'owner' | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (!userDoc.exists()) {
            setShowSignUp(true);
          } else {
            const userData = userDoc.data();
            setUserType(userData.userType);
          }
        } catch (error) {
          console.error('Error checking user document:', error);
          toast.error('Error loading user data');
        }
      } else {
        setUserType(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const createUserDocument = async (user: User, userType: 'dealer' | 'owner', additionalData?: any) => {
    try {
      const userData = {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        createdAt: Date.now(),
        joinedDate: Date.now(),
        emailVerified: user.emailVerified,
        userType,
        ...additionalData
      };

      await setDoc(doc(db, 'users', user.uid), userData);
      setUserType(userType);

      if (userType === 'owner') {
        setShowSignUp(false);
        navigate('/profile');
      } else {
        setShowSignUp(true);
      }
    } catch (error) {
      console.error('Error creating user document:', error);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      const result = await signInWithPopup(auth, provider);
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      
      if (!userDoc.exists()) {
        setShowSignUp(true);
      } else {
        const userData = userDoc.data();
        setUserType(userData.userType);
        if (userData.userType === 'owner') {
          navigate('/profile');
        } else {
          navigate(`/dealer-profile/${result.user.uid}`);
        }
      }
      
      return result.user;
    } catch (error: any) {
      if (error.code === 'auth/popup-blocked') {
        toast.error('Please allow popups for this site to sign in with Google');
      } else {
        toast.error('Failed to sign in with Google');
      }
      throw error;
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const userDoc = await getDoc(doc(db, 'users', result.user.uid));
      
      if (!userDoc.exists()) {
        setShowSignUp(true);
      } else {
        const userData = userDoc.data();
        setUserType(userData.userType);
        if (userData.userType === 'owner') {
          navigate('/profile');
        } else {
          navigate(`/dealer-profile/${result.user.uid}`);
        }
      }
      
      if (!result.user.emailVerified) {
        toast.error('Please verify your email address');
      }
      
      return result.user;
    } catch (error: any) {
      toast.error('Invalid email or password');
      throw error;
    }
  };

  const signUpWithEmail = async (email: string, password: string, userType: 'dealer' | 'owner', additionalData?: any) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      if (additionalData?.displayName) {
        await updateProfile(result.user, { displayName: additionalData.displayName });
      }
      
      await sendEmailVerification(result.user);
      toast.success('Verification email sent! Please check your inbox.');
      
      await createUserDocument(result.user, userType, additionalData);
      
      return result.user;
    } catch (error: any) {
      toast.error(error.message);
      throw error;
    }
  };

  const resendVerificationEmail = async () => {
    if (!user) {
      toast.error('No user signed in');
      return;
    }

    try {
      await sendEmailVerification(user);
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error: any) {
      toast.error('Failed to send verification email. Please try again later.');
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Password reset email sent! Please check your inbox.');
    } catch (error: any) {
      toast.error('Failed to send password reset email');
      throw error;
    }
  };

  const verifyEmail = async (actionCode: string) => {
    try {
      await applyActionCode(auth, actionCode);
      if (user) {
        await setDoc(doc(db, 'users', user.uid), {
          emailVerified: true
        }, { merge: true });
      }
      toast.success('Email verified successfully!');
    } catch (error: any) {
      toast.error('Failed to verify email');
      throw error;
    }
  };

  const confirmPasswordChange = async (actionCode: string, newPassword: string) => {
    try {
      await verifyPasswordResetCode(auth, actionCode);
      await confirmPasswordReset(auth, actionCode, newPassword);
      toast.success('Password updated successfully!');
    } catch (error: any) {
      toast.error('Failed to reset password');
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUserType(null);
      navigate('/', { replace: true });
    } catch (error: any) {
      toast.error('Failed to sign out');
      throw error;
    }
  };

  const completeSignUp = () => {
    setShowSignUp(false);
    if (userType === 'dealer') {
      navigate(`/dealer-profile/${user?.uid}`);
    } else {
      navigate('/profile');
    }
  };

  return {
    user,
    userType,
    loading,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    signOut,
    showSignUp,
    setShowSignUp,
    completeSignUp,
    resendVerificationEmail,
    resetPassword,
    verifyEmail,
    confirmPasswordChange
  };
}